// M1 THROWAWAY (OPTI-6761): execute a SQL query against the prototype
// `/analytics/api/v1/query` endpoint and return the result rows shaped as
// `{ [column]: value }`. Mirrors the prototype `runQuery()` — React Query
// owns the cache. Delete once the proper agent lifecycle lands.

import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import axios from "axios";

import { useCurrentWorkspace } from "src/api/queries";

const METRICS_QUERY_PATH = "/analytics/api/v1/query";

export type MetricsQueryRawResult = {
  columns?: string[];
  rows?: ReadonlyArray<ReadonlyArray<unknown>>;
  error?: string;
};

export type MetricsQueryResponse = {
  results?: Record<string, MetricsQueryRawResult>;
};

export type MetricsQueryRow = Record<string, unknown>;

// Bind values for `/query`, forwarded verbatim from the artifact `parameters`.
export type MetricsQueryParams = Record<string, any>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

// Exact bind names (not any `$`) while bare-SQL charts coexist with parameterized ones.
// TODO(OPTI-8112): once params are mandatory, drop this and gate on params presence.
export const sqlRequiresParams = (sql: string | undefined): boolean =>
  sql !== undefined &&
  /\$(environment|time_range_start|time_range_end)\b/.test(sql);

export const toBindParams = (
  parameters: unknown,
): MetricsQueryParams | undefined =>
  isRecord(parameters) ? (parameters as MetricsQueryParams) : undefined;

export const metricsQueryKeys = {
  all: ["metrics-query"] as const,
  query: (
    baseUrl: string | undefined,
    dataSource: string | undefined,
    sql: string | undefined,
    params?: MetricsQueryParams,
  ) =>
    [
      ...metricsQueryKeys.all,
      baseUrl,
      dataSource,
      sql,
      params ? JSON.stringify(params) : undefined,
    ] as const,
};

const fetchMetricsQuery = async (
  baseUrl: string,
  sql: string,
  params: MetricsQueryParams | undefined,
  dataSource: string | undefined,
): Promise<MetricsQueryRow[]> => {
  const endpoint = `https://${baseUrl}${METRICS_QUERY_PATH}`;
  // Bare string for pre-parameterization agents; `{ sql, params }` once parameterized.
  // TODO(OPTI-8112): always send `{ sql, params }` and drop the param-presence checks.
  const query = params ? { sql, params } : sql;
  const body: {
    queries: Record<
      string,
      string | { sql: string; params: MetricsQueryParams }
    >;
    context?: string;
  } = { queries: { q: query } };
  if (dataSource !== undefined) {
    body.context = dataSource;
  }
  const { data } = await axios.post<MetricsQueryResponse>(endpoint, body);
  const result = data.results?.q;
  if (!result) {
    throw new Error("No result returned from /analytics/api/v1/query");
  }
  if (result.error) {
    throw new Error(result.error);
  }
  const { columns, rows } = result;
  if (!columns || !rows) {
    throw new Error("Result missing columns or rows");
  }
  return rows.map((row) =>
    Object.fromEntries(columns.map((col, i) => [col, row[i]])),
  );
};

export const useMetricsQuery = (
  sql: string | undefined,
  dataSource?: string,
  params?: MetricsQueryParams,
): UseQueryResult<MetricsQueryRow[], Error> => {
  const { data: workspace } = useCurrentWorkspace();
  const baseUrl = workspace?.base_url;

  return useQuery({
    queryKey: metricsQueryKeys.query(baseUrl, dataSource, sql, params),
    queryFn: () => {
      if (!baseUrl) throw new Error("Workspace not loaded");
      if (!sql) throw new Error("No SQL provided");
      return fetchMetricsQuery(baseUrl, sql, params, dataSource);
    },
    // Hold a parameterized query until its params arrive — unbound `$`-SQL would be
    // rejected and churn retries during polling.
    enabled:
      Boolean(baseUrl && sql) &&
      (!sqlRequiresParams(sql) || params !== undefined),
    staleTime: Infinity,
  });
};
